import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { payConfigGateway } from '../index';
import { PayDaySelectors } from '../PayDaySelectors';
import {
  EMPTY_PAY_DAY_SELECTION, formatPaySchedule, frequencyKey, selectionFromSchedule, selectionToPayload, validatePayDaySelection,
  type PayDayField, type PayDaySelection,
} from '../paySchedule';
import { payFrequencyLabels, type PayScheduleResponse } from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

export function PaySchedulesPage() {
  const [schedules, setSchedules] = useState<PayScheduleResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editing, setEditing] = useState<PayScheduleResponse | null>(null);

  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState(2);
  const [payDays, setPayDays] = useState<PayDaySelection>({ ...EMPTY_PAY_DAY_SELECTION, first: '5' });
  const [payDayErrors, setPayDayErrors] = useState<Partial<Record<PayDayField, string>>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSchedules(await payConfigGateway.listPaySchedules());
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load pay schedules.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  function resetForm() {
    setName(''); setFrequency(2); setPayDays({ ...EMPTY_PAY_DAY_SELECTION, first: '5' }); setPayDayErrors({}); setEditing(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    // Only the frequencies the backend supports are offered, so the key is never 'annually' here.
    const key = frequencyKey(frequency) as Exclude<ReturnType<typeof frequencyKey>, 'annually' | null>;
    const errors = validatePayDaySelection(key, payDays);
    setPayDayErrors(errors);
    if (Object.keys(errors).length > 0) return;
    const input = { name, frequency, ...selectionToPayload(key, payDays) };
    try {
      if (editing) {
        await payConfigGateway.updatePaySchedule(editing.id, { ...input, isActive: editing.isActive });
      } else {
        await payConfigGateway.createPaySchedule(input);
      }
      resetForm();
      setShowAddForm(false);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not save pay schedule.'));
    }
  }

  async function handleSetDefault(id: string) {
    setError(null);
    try {
      await payConfigGateway.setDefaultPaySchedule(id);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not set default schedule.'));
    }
  }

  async function handleToggleActive(schedule: PayScheduleResponse) {
    setError(null);
    try {
      await payConfigGateway.updatePaySchedule(schedule.id, {
        name: schedule.name, frequency: schedule.frequency, firstPayDay: schedule.firstPayDay,
        secondPayDay: schedule.secondPayDay, ...(schedule.firstPayDayKind != null && { firstPayDayKind: schedule.firstPayDayKind }),
        ...(schedule.secondPayDayKind != null && { secondPayDayKind: schedule.secondPayDayKind }), isActive: !schedule.isActive,
      });
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not change status.'));
    }
  }

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>Pay Schedules</h2>
        <button type="button" onClick={() => { resetForm(); setShowAddForm((v) => !v); }}>
          {showAddForm ? 'Cancel' : 'Add schedule'}
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      {(showAddForm || editing) && (
        <form className="inline-form" onSubmit={handleSubmit}>
          {editing && <strong>Editing {editing.name}</strong>}
          <input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required />
          <select value={frequency} onChange={(e) => { setFrequency(Number(e.target.value)); setPayDays(EMPTY_PAY_DAY_SELECTION); setPayDayErrors({}); }}>
            {Object.entries(payFrequencyLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <PayDaySelectors
            key={frequency}
            frequency={frequencyKey(frequency)!}
            value={payDays}
            errors={payDayErrors}
            idPrefix="schedule-payday"
            onChange={(field, value) => { setPayDays((p) => ({ ...p, [field]: value })); setPayDayErrors((p) => ({ ...p, [field]: undefined })); }}
          />
          <button type="submit">{editing ? 'Save' : 'Create'}</button>
          {editing && <button type="button" className="link" onClick={resetForm}>Cancel</button>}
        </form>
      )}

      {loading && <p className="muted">Loading…</p>}
      {!loading && schedules.length === 0 && <p className="muted">No pay schedules yet.</p>}

      {!loading && schedules.length > 0 && (
        <table>
          <thead>
            <tr><th>Name</th><th>Frequency</th><th>Pay day</th><th>Default</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {schedules.map((s) => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td>{payFrequencyLabels[s.frequency]}</td>
                <td>{formatPaySchedule(s) || '—'}</td>
                <td>{s.isDefault ? 'Yes' : ''}</td>
                <td>{s.isActive ? 'Active' : 'Inactive'}</td>
                <td>
                  <button type="button" className="link" onClick={() => {
                    setEditing(s); setName(s.name); setFrequency(s.frequency); setPayDays(selectionFromSchedule(s));
                    setPayDayErrors({}); setShowAddForm(false);
                  }}>Edit</button>
                  {!s.isDefault && <button type="button" className="link" onClick={() => void handleSetDefault(s.id)}>Set default</button>}
                  <button type="button" className="link" onClick={() => void handleToggleActive(s)}>
                    {s.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
